import React, {useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {
  Badge,
  Button,
  CountryPicker,
  FilePicker,
  Input,
  Modal,
} from '@/components';
import type {SelectProps} from '@/components/common/Input';
import {Select} from '@/components/common/Input';
import {fileService, kycBucketKey} from '@/services/fileService';
import type {BusinessKYCDocumentType} from '@/types/BusinessKYCDocumentType';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import {idTypes} from './idTypes';
import type {KYCModalFormProps, KYCModalProps} from './types';

const FILE_FIELDS = ['SELFIE', 'PROOF_OF_ADDRESS'];

const DOCUMENT_TYPES = {
  SELFIE: 'Selfie',
  PROOF_OF_ADDRESS: 'Proof of Address',
};

const RepresentativeTypeSelectOptions: SelectProps['options'] = [
  {
    key: 'Ultimate Beneficial Owner (UBO)',
    value: 'Ultimate Beneficial Owner (UBO)',
  },
  {
    key: 'Company Director',
    value: 'Company Director',
  },
  {
    key: 'Company Representative',
    value: 'Company Representative',
  },
];

const KYCProfileForm: React.FC<KYCModalFormProps> = ({
  onSubmit,
  onClose,
  isKycSubmitting,
  user,
  idType,
}) => {
  const [profile, setProfile] = useState<Record<string, string>>({
    COUNTRY: user?.meta?.kyc?.data?.COUNTRY || '',
    REPRESENTATIVE_TYPE: user?.meta?.kyc?.data?.REPRESENTATIVE_TYPE || '',
    DATE_OF_BIRTH: user?.meta?.kyc?.data?.DATE_OF_BIRTH || '',
    ADDRESS: user?.meta?.kyc?.data?.ADDRESS || '',
    ID_FRONT: user?.meta?.kyc?.data?.ID_FRONT || '',
  });
  const [selectedIdType, setSelectedIdType] = useState(idType || '');
  const [selectedIDNumber, setSelectedIDNumber] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploading(true);
    try {
      const {putUrl} = await fileService.uploadFile(
        file.name,
        kycBucketKey,
        user?.id,
      );
      if (!putUrl) {
        throw new Error('Failed to fetch upload link. Retry');
      }
      const response = await fileService.uploadToS3(putUrl, file);
      if (response.status !== 200) {
        throw new Error(`Upload of ID_FRONT to s3 failed`);
      }

      setProfile((prev) => ({
        ...prev,
        ID_FRONT: file.name,
      }));
      toast.success(`ID_FRONT uploaded successfully`);
    } catch (error) {
      toast.error(getErrorMessage(error, ''));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    return onSubmit({
      ...profile,
      [selectedIdType]: selectedIDNumber,
    });
  };

  useEffect(() => {
    if (user && selectedIdType?.trim() !== '') {
      setSelectedIDNumber(
        user?.meta?.kyc?.data?.[selectedIdType as BusinessKYCDocumentType] ||
          '',
      );
    }
  }, [selectedIdType, user]);

  return (
    <>
      <form onSubmit={handleSubmit} data-cy="merchant-kyc-profile-form">
        <div className="p-2">
          <div className="mb-3">
            <CountryPicker
              label="Country"
              value={profile?.COUNTRY}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setProfile((prev) => ({
                  ...prev,
                  COUNTRY: e.target.value,
                }))
              }
              placeholder="--Country--"
            />
          </div>
          <div className="mb-3">
            <Select
              label={'Representative Type'}
              value={profile?.REPRESENTATIVE_TYPE}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setProfile((prev) => ({
                  ...prev,
                  REPRESENTATIVE_TYPE: e.target.value,
                }))
              }
              className="w-full"
              placeholder={`Select Representative Type`}
              options={RepresentativeTypeSelectOptions}
            />
          </div>
          <div className="mb-3">
            <Input
              label={'Date Of Birth'}
              value={profile?.DATE_OF_BIRTH}
              type="date"
              onChange={(e) =>
                setProfile((prev) => ({
                  ...prev,
                  DATE_OF_BIRTH: e.target.value,
                }))
              }
              className="w-full"
              placeholder={`Enter Date Of Birth`}
            />
          </div>
          <div className="mb-3">
            <Input
              label={'Address'}
              value={profile?.ADDRESS}
              type="text"
              onChange={(e) =>
                setProfile((prev) => ({
                  ...prev,
                  ADDRESS: e.target.value,
                }))
              }
              className="w-full"
              placeholder={`Enter Address`}
            />
          </div>
          <div className="mb-3">
            <Select
              label={'ID Type'}
              value={selectedIdType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedIdType(e.target.value)
              }
              className="w-full"
              placeholder={`Select ID Type`}
              options={idTypes}
            />
          </div>
          {selectedIdType?.trim() !== '' && (
            <div className="mb-3">
              <Input
                label={'ID Number'}
                value={selectedIDNumber}
                type="text"
                onChange={(e) => setSelectedIDNumber(e.target.value)}
                className="w-full"
                placeholder={`Enter ID Number`}
              />
            </div>
          )}
          {selectedIdType?.trim() !== '' &&
            (isUploading ? (
              <div className="flex justify-center items-center border border-border rounded-lg py-8 bg-muted">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              <FilePicker
                isLoading={false}
                label={selectedIdType?.replaceAll('_', ' ')}
                name={'ID_FRONT'}
                onChange={handleFileUpload}
                className="mt-0 dark:!bg-transparent"
                accept={'.pdf'}
              />
            ))}
        </div>
        <div className="flex w-full justify-end space-x-2 py-5">
          <Button colorScheme="cyan" onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isKycSubmitting || isUploading}
            type="submit"
            variant="solid"
            colorScheme="cyan"
          >
            Upload
          </Button>
        </div>
      </form>
    </>
  );
};

const KYCDocumentsForm: React.FC<KYCModalFormProps> = ({
  onSubmit,
  isKycSubmitting,
  onClose,
  userId,
}) => {
  const [documents, setDocuments] = useState<Record<string, string>>({});
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  // Derived straight from `documents` over the module-level FILE_FIELDS list,
  // so there is nothing for an effect to synchronize.
  const uploadedDocs = useMemo(
    () => FILE_FIELDS.filter((file) => !!documents[file]),
    [documents],
  );

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDocType) {
      return;
    }

    setUploadingDoc(selectedDocType);
    try {
      const {putUrl} = await fileService.uploadFile(
        file.name,
        kycBucketKey,
        userId,
      );
      if (!putUrl) {
        throw new Error('Failed to fetch upload link. Retry');
      }
      const response = await fileService.uploadToS3(putUrl, file);
      if (response.status !== 200) {
        throw new Error(`Upload of ${selectedDocType} to s3 failed`);
      }

      setDocuments((prev) => ({
        ...prev,
        [selectedDocType]: file.name,
      }));
      toast.success(
        `${DOCUMENT_TYPES[selectedDocType as keyof typeof DOCUMENT_TYPES]} uploaded successfully`,
      );
    } catch (error) {
      toast.error(getErrorMessage(error, ''));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // `onSubmit` (useMerchantKYC.updateUserKYC) toasts its own failures, so
    // this only fires if that handler itself throws. Log rather than swallow.
    onSubmit({
      ...documents,
    }).catch(logRejection('Failed to submit KYC documents'));
  };
  return (
    <form onSubmit={handleSubmit} data-cy="merchant-kyc-documents-form">
      <div className="p-2">
        <div className="space-y-4">
          <div>
            <Select
              label="Select Document To Upload"
              placeholder="Choose a document..."
              dataCy="merchant-kyc-document-type"
              options={FILE_FIELDS.map((field) => ({
                key: DOCUMENT_TYPES[field as keyof typeof DOCUMENT_TYPES],
                value: field,
              }))}
              value={selectedDocType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedDocType(e.target.value)
              }
            />
          </div>

          {selectedDocType && (
            <div>
              {uploadingDoc === selectedDocType ? (
                <div className="flex justify-center items-center border border-border rounded-lg py-8 bg-muted">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : (
                <FilePicker
                  isLoading={false}
                  label={
                    DOCUMENT_TYPES[
                      selectedDocType as keyof typeof DOCUMENT_TYPES
                    ]
                  }
                  name={selectedDocType}
                  onChange={handleFileUpload}
                  className="mt-0 dark:!bg-transparent"
                  accept={
                    selectedDocType === 'SELFIE' ? '.png,.jpg,.jpeg' : '.pdf'
                  }
                />
              )}
              {uploadedDocs?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {uploadedDocs.map((doc) => (
                    <Badge className="text-xs" colorScheme="blue" key={doc}>
                      {doc}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex w-full justify-end space-x-2 py-5">
        <Button colorScheme="cyan" onClick={onClose} variant="outline">
          Cancel
        </Button>
        <Button
          disabled={
            isKycSubmitting ||
            uploadingDoc === selectedDocType ||
            typeof documents[selectedDocType || ''] === 'undefined'
          }
          type="submit"
          variant="solid"
          colorScheme="cyan"
        >
          Upload
        </Button>
      </div>
    </form>
  );
};

const KYCModal: React.FC<KYCModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isKycSubmitting,
  type = 'Profile',
  userId,
  user,
  idType,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title="Upload KYC Details"
      dataCy="merchant-kyc-modal"
    >
      {type === 'Profile' ? (
        <KYCProfileForm
          isKycSubmitting={isKycSubmitting}
          onSubmit={onSubmit}
          onClose={onClose}
          userId={userId}
          user={user}
          idType={idType}
        />
      ) : (
        <KYCDocumentsForm
          isKycSubmitting={isKycSubmitting}
          onSubmit={onSubmit}
          userId={userId}
          onClose={onClose}
          idType={null}
        />
      )}
    </Modal>
  );
};

export default KYCModal;
